import { Component, inject } from '@angular/core';
import { GroupKeyDto } from '@models/clash/group-key-dto';
import { InscricaoEtapaDto } from '@models/embate/clash-dto';
import { EventTypeEnum } from '@models/enums/event-type-enum';
import { IPageable } from '@models/pagination/pageable';
import { ButtonType } from '@shared/components/button/button.component';
import { GenderPipe } from '@shared/pipes/naipe.pipe';
import { ClashService } from '@shared/services/clash.service';
import {
  GroupKeyService,
  InscricaoChaveDto,
  InscricaoChaveForm,
  InscricaoEtapaCompletaDto,
} from '@shared/services/competition/group-key.service';
import { StepParameterizationService } from '@shared/services/competition/step-parametrization.service';
import {
  ConfirmationModalService,
  ConfirmationModalType,
} from '@shared/services/confirmation-modal.service';
import { EventService } from '@shared/services/event.service';
import { AttachmentService } from '@shared/services/attachment.service';
import { FullscreenService } from '@shared/services/fullscren.service';
import { LoadingService } from '@shared/services/loading.service';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  finalize,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { BaseComponent } from 'src/app/core/utilities/base-component/base-component';

@Component({
  selector: 'sigecom-key-management',
  templateUrl: './key-management.component.html',
})
export class KeyManagementComponent extends BaseComponent {
  readonly #loadingService = inject(LoadingService);
  readonly #keyEnrollmentService = inject(GroupKeyService);
  readonly #clashService = inject(ClashService);
  readonly #stepParametrizationsService = inject(StepParameterizationService);
  readonly #attachmentService = inject(AttachmentService);
  readonly #fullscreenService = inject(FullscreenService);
  readonly #eventService = inject(EventService);
  readonly #confirmationModalService = inject(ConfirmationModalService);

  private readonly keysSubject = new BehaviorSubject<GroupKeyDto[]>([]);
  private checkCabecasSubject = new BehaviorSubject<void>(undefined);
  public readonly keys$ = this.keysSubject.asObservable();
  public canMakeChange = false;

  public readonly petNrId = Number(
    this.activatedRoute.snapshot.paramMap.get('petNrId'),
  );

  public petDescription = '';
  public bannerPreview?: string;
  public drawDisabled = false;
  filteredCabecas$?: Observable<InscricaoEtapaCompletaDto[]>;
  public keyEnrollmentsDto: InscricaoEtapaCompletaDto[] = [];
  public qtdIncSelecionados = 0;
  public qtdTotalInc = 0;
  public qtdCabecaChavesPorChave = 0;
  public enrollmentStates$?: Observable<
    { estTxSigla: string; buttonType: ButtonType }[]
  >;
  public submittingCabecaChave: number | null = null;

  public drawModalOpen = false;
  public drawedIncTemp?: InscricaoChaveDto;

  modalStateChange = false;

  public emptyHeadList = false;
  public cabecasDeChaveList$?: Observable<InscricaoEtapaCompletaDto[]>;
  public cabecasDeChaveList: InscricaoEtapaCompletaDto[] = [];
  public isNational = false;

  cheNrIdModalAddCabecaChave?: number;
  cheTxNomeModalAddCabecaChave?: string;
  ichNrIdCabecaChaveAtual?: number;

  cabecaChaveModalOpen = false;

  click$ = new Subject<void>();

  public todosCabecasSelecionados$: Observable<boolean> =
    this.checkCabecasSubject.pipe(
      switchMap(() =>
        this.#keyEnrollmentService.verificarTodosCabecasDeChaveSelecionados(
          this.petNrId,
        ),
      ),
      map((res) => res.response ?? false),
    );

  ngOnInit(): void {
    this.simpleDraw();
  }

  public makeChange() {
    this.#loadingService.activate();
    this.#keyEnrollmentService
      .fazerTroca(this.petNrId)
      .pipe(this.takeUntilDestroy())
      .subscribe({
        next: (res) => {
          this.#loadingService.deactivate();
          this.canMakeChange = false;
          setTimeout(_=>{ this.snackbarService.showSuccess("Troca realizada! Realize o sorteio novamente para preencher a vaga") },500)
          this.loadKeys();
          this.loadEnrollments()
        },
        error: (err) => {
          this.#loadingService.deactivate();
          this.canMakeChange = false;
          this.messageErrorResolver.resolveMessage(err);
        },
      });
  }

  private loadEnrollments() {
    this.enrollmentStates$ = this.#keyEnrollmentService
      .listarInscricoesCompletas(this.petNrId, {
        page: 0,
        size: 999,
      } as IPageable)
      .pipe(
        tap((incDto) => {
          this.keyEnrollmentsDto = incDto.content ?? [];
          this.#loadingService.activate();
        }),
        map(() => {
          const list = this.keyEnrollmentsDto;
          let result: { estTxSigla: string; buttonType: ButtonType }[] = [];
          this.qtdIncSelecionados = 0;
          list.forEach((inc) => {
            const sorteado = inc.ichNrId !== null;
            let type: ButtonType;
            if (sorteado) {
              type = 'not-selected';
              this.qtdIncSelecionados++;
            } else {
              type = 'selected';
            }
            const nomeEnrollments = this.getDisplaySiglaStateSede(inc);

            result.push({
              estTxSigla: nomeEnrollments,
              buttonType: type,
            });
          });

          this.verifyAllParticipantsWereSelected();
          this.qtdTotalInc = result.length;

          return result;
        }),
        tap(() => {
          this.#loadingService.deactivate();
        }),
      );
  }

  private loadKeys() {
    this.#loadingService.activate();
    this.#clashService
      .getClashs({ petNrId: this.petNrId }, { page: 0, size: 999 })
      .pipe(
        map((keys) => {
          const content = keys.content ?? [];
          const transformedContent = content.map((key) => ({
            ...key,
            inscricoesChave: key.inscricoesChave?.map((inc) => ({
              ...inc,
              estTxSigla: this.getDisplaySiglaStateSede(inc),
            })),
          }));
          this.keysSubject.next(transformedContent);
        }),
        finalize(() => this.#loadingService.deactivate()),
      )
      .subscribe({
        error: (error) => this.messageErrorResolver.resolveMessage(error),
      });
  }

  public getEmptyKeys(item: GroupKeyDto) {
    const C = item.inscricoesChave?.some((inc) => inc.ineBlCabecaChave) ? 0 : 1;
    const len = item.cheNrQtdInc - item.inscricoesChave?.length! - C;
    return Array.from({ length: len });
  }

  public simpleStepParametrization$ = this.#stepParametrizationsService
    .getSimpleStepParametrization$(
      { petNrId: this.petNrId },
      { page: 0, size: 100 },
    )
    .pipe(
      switchMap((step) => {
        const eveTxNome = step.content?.at(0)?.eveTxNome;
        const catTxNome = step.content?.at(0)?.catTxNome;
        const genderPipe = new GenderPipe();
        const catTxSexo = genderPipe.transform(step.content?.at(0)?.catTxSexo);
        this.petDescription = eveTxNome + ' - ' + catTxSexo;
        return this.#stepParametrizationsService.getStepParametrizationById$(
          this.petNrId,
        );
      }),
      map((step) => {
        const objCategoria = step.petObjCategoria;
        this.loadBanner(step.petTxBanner);
        this.qtdCabecaChavesPorChave = step.petNrQuantidadeCabecasDeChave!;
        const petTxNome = step.petTxNome;
        return {
          catNrId: objCategoria?.catNrId,
          categoriaInfo: objCategoria
            ? objCategoria?.catNrAnoInicio + ' - ' + objCategoria?.catNrAnoFim
            : '',
          petTxNome: petTxNome,
          formatoInfo: objCategoria?.catTxTipoInscricao || '',
          modalidadeInfo: objCategoria?.catTxNome || '',
        };
      }),
      switchMap((info) => {
        this.isNational = true;
        return this.#eventService
          .getEventByCatNrId(info.catNrId!)
          .pipe(map((event) => ({ info, event })));
      }),
      map(({ info, event }) => {
        this.isNational = event.eveTxTipo === EventTypeEnum.NACIONAL;
        return info;
      }),
      tap(() => {
        this.refresh();
      }),
    );

  private loadBanner(referenciaBanner?: string): void {
    this.bannerPreview = undefined;
    if (!referenciaBanner) return;

    this.#attachmentService
      .getAttachmentByUrl(referenciaBanner)
      .pipe(this.takeUntilDestroy())
      .subscribe({
        next: (base64) => (this.bannerPreview = base64),
        error: () => (this.bannerPreview = undefined),
      });
  }

  closeDrawModal() {
    this.drawModalOpen = !this.drawModalOpen;
    this.drawedIncTemp = undefined;
    this.modalStateChange = false;
    this.refresh();
  }

  private simpleDraw() {
    this.click$
      .pipe(
        exhaustMap(() => {
          this.#loadingService.activate();
          this.drawDisabled = true;

          return this.#keyEnrollmentService
            .vincularSorteioNaoCabecaChave(this.petNrId)
            .pipe(
              finalize(() => {
                this.drawDisabled = false;
                this.#loadingService.deactivate();
              }),
            );
        }),
      )
      .subscribe({
        next: (incChave) => {
          this.drawedIncTemp = incChave.response!;
          this.canMakeChange = incChave.response.needsSedeSwap;
          if (this.canMakeChange) {
            setTimeout((_) => {
              this.snackbarService.showDanger(
                "O sorteio não pôde ser concluído porque a segunda equipe do estado sede não tem uma chave válida. Clique em 'Fazer Troca' para corrigir automaticamente.",
              );
            }, 1200);
            setTimeout((_) => {
              this.closeDrawModal();
            }, 1800);
          }
          this.drawModalOpen = true;
        },
        error: (error) => {
          this.messageErrorResolver.resolveMessage(error);
        },
      });
  }

  changeModalStyle() {
    this.modalStateChange = true;
  }

  private refresh() {
    this.loadKeys();
    this.loadEnrollments();
    this.checkCabecasSubject.next();
  }

  trackStateById(_: number, item: any) {
    return item.estTxSigla;
  }

  trackClashById(_: number, item: GroupKeyDto) {
    return item.cheNrId;
  }

  trackCabecaCahveById(_: number, item: InscricaoEtapaDto) {
    return item.ineNrId;
  }

  trackById(index: number, item: any): number {
    return item.ineNrId; // ou outro ID único
  }

  private loadCabecasDeChave(estTxDescricao?: string) {
    this.#loadingService.activate();
    this.cabecasDeChaveList$ = this.#keyEnrollmentService
      .listarInscricoesCompletas(
        this.petNrId,
        { page: 0, size: 999 },
        estTxDescricao,
      )
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        catchError((err) => {
          this.messageErrorResolver.resolveMessage(err);
          return of(null);
        }),
        filter((value) => value !== null),
        map((keys) => {
          const content = keys?.content ?? [];
          this.emptyHeadList = keys?.empty ?? false;
          return content;
        }),
        tap((content) => (this.cabecasDeChaveList = content)),
        tap(() => this.#loadingService.deactivate()),
      );
  }

  searchCabecasDeChaveByName(event: Event) {
    const input = event.target as HTMLInputElement;
    const termo = input.value.toLowerCase();

    this.loadCabecasDeChave(termo);
  }

  openCabecaChaveModal(cheNrId: number, ichNrId?: number, cheTxNome?: string) {
    this.loadCabecasDeChave();
    this.cheNrIdModalAddCabecaChave = cheNrId;
    this.cheTxNomeModalAddCabecaChave = cheTxNome;
    this.ichNrIdCabecaChaveAtual = ichNrId;
    this.cabecaChaveModalOpen = true;
  }

  closeModalCabecaChave() {
    this.cabecaChaveModalOpen = false;
    this.refresh();
  }

  addCabecaChave(ineNrIdOrFedNrId: number, ichBlSede?: boolean) {
    if (this.submittingCabecaChave !== null) return;

    const form: InscricaoChaveForm = {
      cheNrId: this.cheNrIdModalAddCabecaChave!,
      ineNrId: !this.isNational ? ineNrIdOrFedNrId : undefined,
      fedNrId: this.isNational ? ineNrIdOrFedNrId : undefined,
      ichNrIdCabecaChaveAtual: this.ichNrIdCabecaChaveAtual,
      ichBlSede: ichBlSede ?? false,
    };
    this.#keyEnrollmentService
      .vincularManual(form)
      .pipe(finalize(() => (this.submittingCabecaChave = null)))
      .subscribe({
        next: () => {
          this.closeModalCabecaChave();
          this.snackbarService.showSuccess('Participante adicionado');
        },
        error: (error) => this.messageErrorResolver.resolveMessage(error),
      });
  }

  generalDraw() {
    this.#keyEnrollmentService
      .vincularSorteioTodosParticipantesDisponiveis(this.petNrId)
      .subscribe({
        next: () => {
          this.refresh();
          this.snackbarService.showSuccess('Sorteio geral concluído');
          this.verifyAllParticipantsWereSelected();
        },
        error: (error) => {
          this.messageErrorResolver.resolveMessage(error);
        },
      });
  }

  protected showConfirmationModal() {
    this.#confirmationModalService.openModal({
      modalType: ConfirmationModalType.danger,
      cancelText: 'Cancelar',
      confirmText: 'reinicializar',
      message:
        'Deseja realmente reinicializar o sorteio? Todos os participantes serão removidos das chaves e o sorteio terá que ser feito novamente',
      callbackExecuteAction: () =>
        this.#keyEnrollmentService.resetarSorteio(this.petNrId).subscribe({
          next: () => {
            this.refresh();
            this.snackbarService.showSuccess(
              'Sorteio reinicializado com sucesso',
            );
          },
          error: (error) => this.messageErrorResolver.resolveMessage(error),
        }),
    });
  }

  hasHeadPlayer(item: GroupKeyDto) {
    let result: boolean = false;
    item.inscricoesChave?.forEach((inc) => {
      if (inc.ineBlCabecaChave) {
        result = true;
      }
    });
    return result;
  }

  toogleFullScreen() {
    this.#fullscreenService.toggle();
  }

  getSlots(item: any): { isHead: boolean }[] {
    const total = item.cheNrQtdInc || 0;
    const maxHeads = this.qtdCabecaChavesPorChave || 0;

    return Array.from({ length: total }, (_, index) => ({
      isHead: index < maxHeads,
    }));
  }

  verifyAllParticipantsWereSelected() {
    this.drawDisabled = this.keyEnrollmentsDto.every(
      (inc) => inc.ineBlSorteado,
    );
  }

  getDisplayNameStateSede(inc: InscricaoEtapaCompletaDto): string {
    if (this.isNational) {
      if (inc.ichBlSede) {
        return '1 - ' + inc.estTxDescricao;
      } else {
        return inc.estTxDescricao;
      }
    } else {
      return inc.incTxResumo;
    }
  }
  getDisplaySiglaStateSede(
    inc: InscricaoEtapaCompletaDto | InscricaoChaveDto,
  ): string {
    if (this.isNational) {
      if (inc.ichBlSede) {
        return '1 - ' + inc.estTxSigla;
      } else {
        return inc.estTxSigla;
      }
    } else {
      const resumo =
        (inc as InscricaoChaveDto).inscricaoEtapaDto?.ineObjInscricao
          ?.incTxResumo ||
        (inc as InscricaoEtapaCompletaDto).incTxResumo ||
        '';
      return resumo;
    }
  }
}
